import React from 'react';
import { clsx } from 'clsx';

export interface AvatarProps {
  src?: string;
  alt: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fallback?: string;
  status?: 'online' | 'offline' | 'away' | 'busy';
  className?: string;
}

const sizeStyles = {
  xs: 'h-6 w-6 text-xs',
  sm: 'h-8 w-8 text-sm',
  md: 'h-10 w-10 text-base',
  lg: 'h-12 w-12 text-lg',
  xl: 'h-16 w-16 text-2xl',
};

const statusStyles = {
  online: 'bg-green-500',
  offline: 'bg-gray-400',
  away: 'bg-amber-500',
  busy: 'bg-red-500',
};

export function Avatar({ src, alt, size = 'md', fallback, status, className }: AvatarProps) {
  const [imageError, setImageError] = React.useState(false);

  const showFallback = !src || imageError;
  const initials =
    fallback ||
    alt
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

  return (
    <div className={clsx('relative inline-block', className)}>
      <div
        className={clsx(
          'overflow-hidden rounded-full bg-gray-200 dark:bg-dark-700',
          'flex items-center justify-center font-medium',
          'text-gray-600 dark:text-gray-300',
          sizeStyles[size]
        )}
      >
        {showFallback ? (
          <span>{initials}</span>
        ) : (
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
          />
        )}
      </div>

      {status && (
        <span
          className={clsx(
            'absolute bottom-0 right-0 block rounded-full ring-2 ring-white dark:ring-dark-800',
            statusStyles[status],
            size === 'xs'
              ? 'h-1.5 w-1.5'
              : size === 'sm'
                ? 'h-2 w-2'
                : size === 'md'
                  ? 'h-2.5 w-2.5'
                  : size === 'lg'
                    ? 'h-3 w-3'
                    : 'h-4 w-4'
          )}
          aria-label={`Status: ${status}`}
        />
      )}
    </div>
  );
}
